/**
 * JobsGridOptimized.tsx
 * 
 * Performance-optimized version of JobsGrid with ALL FILTERS DISABLED
 * This is for testing to measure the performance impact of filters
 */

import React, { useState, useEffect, useCallback } from "react";
import * as XLSX from 'xlsx';
import dayjs from "dayjs";
import { Grid, GridColumn, GridSortChangeEvent, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Switch } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import {
  FaFileExcel,
  FaCheckCircle,
  FaFilePdf,
  FaStickyNote,
  FaWrench,
  FaExclamationTriangle,
  FaPlusCircle,
} from "react-icons/fa";

import "./Grids.css";
import { jobsGrid } from "../../data/interfaces";
import { DateFormatter } from "../../data/helper";
import { getJobsWithPagination } from "../../data/jobData";

const formatBool = (val: boolean | null | undefined): string =>
  val === true ? "YES" : val === false ? "NO" : "";

function JobsGridOptimized(props: any) {
  const dateFormatter = new DateFormatter();
  
  // State for Kendo Grid
  const [data, setData] = useState<jobsGrid[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(20);
  
  // Sorting state
  const [sort, setSort] = useState<SortDescriptor[]>([]);
  
  // Toggle switches
  const [showCompleted, setShowCompleted] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showRemedialWorks, setShowRemedialWorks] = useState(false);
  const [showEngineerComments, setShowEngineerComments] = useState(false);
  
  // Allocation type filter
  const allocationTypes = [
    { text: "All Items", value: "all" },
    { text: "Allocated", value: "allocated" },
    { text: "Unallocated", value: "unallocated" }
  ];
  const [allocationType, setAllocationType] = useState(allocationTypes[0]);

  const handleShowCompleted = (event: any) => {
    setShowCompleted(event.target.value);
    if (props.onShowCompleted) {
      props.onShowCompleted(event.target.value);
    }
  };

  const handleShowDeleted = (event: any) => {
    setShowDeleted(event.target.value);
    if (props.onShowDeleted) {
      props.onShowDeleted(event.target.value);
    }
  };

  const handleShowRemedialWorks = (event: any) => {
    setShowRemedialWorks(event.target.value);
    if (props.onShowRemedialWorks) {
      props.onShowRemedialWorks(event.target.value);
    }
  };

  const handleShowEngineerComments = (event: any) => {
    setShowEngineerComments(event.target.value);
    if (props.onShowEngineerComments) {
      props.onShowEngineerComments(event.target.value);
    }
  };

  const handleAllocationTypeChange = (event: any) => {
    setAllocationType(event.target.value);
  };

  // Load data function - NO FILTER PARAMETER
  const loadData = useCallback(async () => {
    setLoading(true);
    console.log('🚀 OPTIMIZED: Loading data with:', { skip, take, showCompleted, showDeleted, showRemedialWorks, showEngineerComments, allocationType: allocationType.value });
    const startTime = performance.now();
    try {
      const result = await getJobsWithPagination({
        skip,
        take,
        sort: sort.map(s => ({ field: s.field!, dir: s.dir! })),
        filter: undefined, // NO FILTERING
        showCompleted,
        showDeleted,
        showRemedialWorks,
        showEngineerComments,
        allocationType: allocationType.value,
      });
      
      const endTime = performance.now();
      console.log('✅ OPTIMIZED: Data loaded in', Math.round(endTime - startTime), 'ms', { dataCount: result.data.length, total: result.total });
      setData(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error('❌ OPTIMIZED: Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [skip, take, sort, showCompleted, showDeleted, showRemedialWorks, showEngineerComments, allocationType]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData, props.refreshKey]);

  // Handle page change
  const handlePageChange = (event: GridPageChangeEvent) => {
    console.log('Page change:', event.page);
    setSkip(event.page.skip);
    setTake(event.page.take);
  };

  // Handle sort change
  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
    setSkip(0); // Reset to first page when sorting changes
  };

  const exportToExcel = () => {
    const exportData = data.map(base => {
      const full = (props.onExportData as any[])?.find(e => e.jobId === base.jobId) || {};
      return {
        ...base,
        note1Details: full.note1Details || "",
        note2Details: full.note2Details || "",
        note3Details: full.note3Details || "",
        note1Completed: formatBool(full.note1Completed),
        note2Completed: formatBool(full.note2Completed),
        note3Completed: formatBool(full.note3Completed),
      };
    });

    const headers = [
      "jobsheetCount", "jobsheetReferralCount", "noteCount", "bookedStartDate", "bookedEndDate", 
      "jobId", "ref", "refFormatted", "description", "startDate", "endDate", "client", "site", 
      "worktype", "priority", "priorityId", "createdDate", "deletedDate", "completedDate", 
      "createdBy", "employeeProjectmanager", "scheduledEndDate", "isBooked", "scheduledStartDate", 
      "note1Details", "note1Completed", "note2Details", "note2Completed", "note3Details", "note3Completed"
    ];
    
    const ws = XLSX.utils.json_to_sheet(exportData, { header: headers });
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Jobs");
    const dateStr = dateFormatter.format(new Date().toDateString());
    XLSX.writeFile(wb, `jobs_optimized_${dateStr}.xlsx`);
  };

  // Row render with conditional formatting
  const rowRender = (tr: React.ReactElement, rowProps: any) => {
    const bgColor = () => {
      if (rowProps.dataItem.deletedDate) {
        return "rgba(235, 199, 199, 0.5)";
      } else if (rowProps.dataItem.completedDate) {
        return "#d7edd7";
      } else {
        return "rgb(245 249 251)";
      }
    };

    const textColor = rowProps.dataItem.deletedDate ? "rgb(137 5 5)" : "#1e2a4e";

    return React.cloneElement(tr, {
      ...tr.props,
      style: {
        ...tr.props.style,
        backgroundColor: bgColor(),
        color: textColor,
        cursor: 'pointer',
      },
      onDoubleClick: () => {
        if (props.onEditToggle) {
          props.onEditToggle(rowProps.dataItem.jobId);
        }
      },
    }, tr.props.children);
  };

  // Custom cell renderer for refFormatted column
  const RefFormattedCell = (cellProps: any) => {
    const jobId = cellProps.dataItem.jobId;
    const row = cellProps.dataItem;

    const jobsheetHandler = () => {
      if (props.onJobsheet) {
        props.onJobsheet(jobId);
      }
    };

    return (
      <td>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Tooltip anchorElement="target" position="top">
            <FaFilePdf
              title="Generate a Jobsheet"
              style={{
                fontSize: "14pt",
                marginRight: "1em",
                cursor: "pointer",
                color: row.isBooked ? "rgb(30, 42, 78)" : "#e9530d",
              }}
              onClick={jobsheetHandler}
            />
          </Tooltip>
          <span
            style={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "5px"
            }}
          >
            {row.refFormatted}
            {row.noteCount > 0 && <FaStickyNote />}
          </span>
          <span style={{ marginLeft: "10px" }}>
            {row.jobsheetCount > 0 && row.jobsheetReferralCount === 0 && <FaWrench />}
          </span>
          <span style={{ marginLeft: "10px" }}>
            {row.jobsheetReferralCount > 0 && <FaWrench fill={"#e9530d"} />}
          </span>
          <span style={{ marginLeft: "10px" }}>
            {row.highPriority && (
              <Tooltip anchorElement="target" position="top">
                <FaExclamationTriangle 
                  fill={"#e9540d"} 
                  title="High Priority Job"
                  style={{ fontSize: "12pt" }}
                />
              </Tooltip>
            )}
          </span>
        </div>
      </td>
    );
  };

  // Custom cell renderer for site column
  const SiteCell = (cellProps: any) => (
    <td>
      <Tooltip anchorElement="target" position="top">
        <span title={cellProps.dataItem.site} className="overflow__container">
          {cellProps.dataItem.site}
        </span>
      </Tooltip>
    </td>
  );

  // Custom cell renderer for description column
  const DescriptionCell = (cellProps: any) => (
    <td>
      <Tooltip anchorElement="target" position="top">
        <span title={cellProps.dataItem.description} className="overflow__container">
          {cellProps.dataItem.description}
        </span>
      </Tooltip>
    </td>
  );

  // Custom cell renderer for date columns
  const DateCell = (cellProps: any) => (
    <td>
      {cellProps.dataItem[cellProps.field] 
        ? dayjs(cellProps.dataItem[cellProps.field]).format("DD-MM-YYYY HH:mm") 
        : ""}
    </td>
  );

  return (
    <div className="p-2">
      {/* Header */}
      <div className="grid__title__container" style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
        <div className="grid__header__background">
          <div className="grid__title">
            <div>Jobs (Optimized - No Filters)</div>
            <div style={{display: "flex", flexDirection: "row"}}>
              {props.lockEdit === false && props.onAddToggle && (
                <div onClick={props.onAddToggle} className="grid__addrecord__button">
                  <FaPlusCircle style={{ fontSize: "14pt", marginRight: "1rem" }} />{" "}
                  <p style={{ margin: 0 }}>Add new job</p>
                </div>
              )}
              <div onClick={exportToExcel} className="grid__export__button">
                <FaFileExcel style={{ fontSize: "14pt", marginRight: "1rem" }} />{" "}
                <p style={{ margin: 0 }}>Export to Excel</p>
              </div>
            </div>
          </div>
          
          <div className="jobs__header__controls" style={{ marginTop: "1rem" }}>
            <div className="jobs__header__control">
              <label>Show Completed:</label>
              <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowCompleted}
                defaultChecked={false}
              />
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaCheckCircle
                  style={{
                    fontSize: "14pt",
                    marginLeft: "0.3rem",
                    color: showCompleted ? "green" : "rgba(255, 255, 255, 0.5)",
                  }}
                  title="Show Completed Jobs"
                />
              </Tooltip>
            </div>

            <div className="jobs__header__control">
              <label>Show Deleted:</label>
              <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowDeleted}
                defaultChecked={false}
              />
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaCheckCircle
                  style={{
                    fontSize: "14pt",
                    marginLeft: "0.3rem",
                    color: showDeleted ? "red" : "rgba(255, 255, 255, 0.5)",
                  }}
                  title="Show Deleted Jobs"
                />
              </Tooltip>
            </div>

            <div className="jobs__header__control">
              <label>Show Remedial Works:</label>
              <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowRemedialWorks}
                defaultChecked={false}
              />
            </div>

            <div className="jobs__header__control">
              <label>Show Engineer Comments:</label>
              <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowEngineerComments}
                defaultChecked={false}
              />
            </div>

            <div className="jobs__header__control">
              <label>Allocation Type:</label>
              <DropDownList
                data={allocationTypes}
                textField="text"
                dataItemKey="value"
                value={allocationType}
                onChange={handleAllocationTypeChange}
                style={{ width: '150px' }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="h-2" />

      {/* Grid Container with Loading Overlay */}
      <div style={{ position: "relative" }}>
        {/* Loading Indicator Overlay */}
        {loading && (
          <div style={{ 
            position: "absolute", 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: "rgba(255, 255, 255, 0.8)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            zIndex: 1000,
            borderRadius: "8px"
          }}>
            <div style={{ textAlign: "center" }}>
              <div className="spinner" />
              <div style={{ marginTop: "1rem", color: "#1e2a4e", fontWeight: 500 }}>
                Loading jobs...
              </div>
            </div>
          </div>
        )}

        {/* Grid - NO FILTERING */}
        <Grid
          data={data}
          total={total}
          skip={skip}
          take={take}
          pageable={{
            buttonCount: 5,
            info: true,
            type: 'numeric',
            pageSizes: [10, 20, 50, 100],
            previousNext: true,
          }}
          sortable
          sort={sort}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          rowRender={rowRender}
          resizable={true}
          style={{ height: 'calc(100vh - 300px)', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}
        >
        <GridColumn 
          field="refFormatted" 
          title="Job No" 
          width="200px"
          cell={RefFormattedCell}
        />
        <GridColumn 
          field="client" 
          title="Client" 
          width="200px"
        />
        <GridColumn 
          field="site" 
          title="Site" 
          width="250px"
          cell={SiteCell}
        />
        <GridColumn 
          field="description" 
          title="Description" 
          width="300px"
          cell={DescriptionCell}
        />
        <GridColumn 
          field="worktype" 
          title="Type of Work" 
          width="180px"
        />
        <GridColumn field="createdDate" title="Date Created" width="150px" />
        <GridColumn 
          field="bookedStartDate" 
          title="Booked Start Date" 
          width="180px"
          cell={DateCell}
        />
        <GridColumn 
          field="bookedEndDate" 
          title="Booked End Date" 
          width="180px"
          cell={DateCell}
        />
        <GridColumn 
          field="employeeProjectmanager" 
          title="Assigned To" 
          width="180px"
        />
        <GridColumn 
          field="employeesAssignedTo" 
          title="Assigned Employee" 
          width="180px"
        />
      </Grid>
      </div>
    </div>
  );
}

export default JobsGridOptimized;
